/*
PURPOSE: UI for creating gestures (person-to-person playful interactions)

USE CASES:
- Send 4 types of gestures: speak_to, smile, nod, boogie
- One-click gesture sending with optional note
- Delightful, minimal UX with animated feedback
- Development selector for testing different animation styles

DOMAIN BOUNDARIES:
- Handles gesture creation UI and form state
- Uses createContentItem API to send gestures (async, non-blocking)
- Should NOT handle comment/reaction functionality
- Manages validation and user input

ASSUMPTIONS:
- Target user/artist must be provided via props
- Contributor-only access (checked by parent)
- Gesture stored as content with relatedData.gestureType
- Text can be empty for non-speak_to gestures
*/

import React, { useState } from 'react';
import { createContentItem } from '../../api/api';
import { useAuthContext } from '../Auth/AuthContext/AuthContext';
import { useDancefloorTheme } from '../../hooks/useDancefloorTheme';
import { trackEvent, EventName } from 'shared/lib/eventTracking';
import { GestureType, ContentItem, OriginLocation } from 'shared/types/contentTypes';
import { gestureThemes, GestureThemeKey } from 'shared/constants/gestureThemes';

interface GestureCreationPanelProps {
  targetUserId: string;
  targetDisplayName: string;
  targetEntityType: 'user' | 'artist' | 'community';
  onGestureCreated?: (gesture: ContentItem) => void;
  originLocation?: OriginLocation;
  onShareMusicClick?: () => void;
}

const GestureCreationPanel: React.FC<GestureCreationPanelProps> = ({
  targetUserId,
  targetDisplayName,
  targetEntityType,
  onGestureCreated,
  originLocation,
  onShareMusicClick
}) => {
  const [gestureText, setGestureText] = useState('');
  const [animatingButton, setAnimatingButton] = useState<GestureType | null>(null);
  // Track which one-time gestures have been sent (persists until refresh)
  const [sentGestures, setSentGestures] = useState<Set<GestureType>>(new Set());
  // Track if currently sending (prevents double-clicks during API call)
  const [isSending, setIsSending] = useState(false);
  const { userProfile } = useAuthContext();

  // Get target user's theme to store at gesture creation time
  const targetTheme = useDancefloorTheme({ userId: targetUserId });

  // Find theme key by comparing theme objects
  const targetThemeKey: GestureThemeKey = (Object.keys(gestureThemes) as GestureThemeKey[])
    .find(key => gestureThemes[key] === targetTheme) || 'default';

  // Use target user's theme for gesture labels
  // Split speak_to from other quick gestures
  const quickGestureTypes = [
    {
      type: 'smile' as GestureType,
      label: targetTheme.gestures.smile.label,
      animationClass: 'gesture-button-smile',
    },
    {
      type: 'nod' as GestureType,
      label: targetTheme.gestures.nod.label,
      animationClass: 'gesture-button-nod',
    },
    {
      type: 'boogie' as GestureType,
      label: targetTheme.gestures.dance_with.label,
      animationClass: 'gesture-button-boogie',
    }
  ];

  const speakToGesture = {
    type: 'speak_to' as GestureType,
    label: targetTheme.gestures.speak_to.label,
  };

  const handleGestureSend = async (gestureType: GestureType, label: string) => {
    if (!userProfile?.userId) {
      return;
    }

    // Prevent double-click during send
    if (isSending) return;

    // One-time gestures (smile, nod, boogie) can only be sent once per session
    if (gestureType !== 'speak_to' && sentGestures.has(gestureType)) return;

    // Validation: speak_to requires text
    if (gestureType === 'speak_to' && !gestureText.trim()) {
      // For speak_to without text, just focus the input
      return;
    }

    // Disable buttons during API call
    setIsSending(true);

    // Trigger animation
    setAnimatingButton(gestureType);

    // Get animation duration based on gesture type
    const animationDuration = gestureType === 'smile' ? 1200 : gestureType === 'boogie' ? 1000 : 800;

    // Wait for animation to complete
    setTimeout(() => {
      setAnimatingButton(null);
    }, animationDuration);

    // Clear text immediately
    const textToSend = gestureText.trim();
    setGestureText('');

    try {
      // Send to backend
      const createdGesture = await createContentItem({
        contentType: 'gesture',
        text: textToSend || `sent a ${label.toLowerCase()}`,
        entityType: targetEntityType,
        entityId: targetUserId,
        relatedData: {
          gestureType: gestureType,
          targetUser: {
            userId: targetUserId,
            displayName: targetDisplayName
          },
          targetTheme: targetThemeKey,
          senderUser: {
            userId: userProfile.userId,
            displayName: userProfile.displayName
          }
        },
        originLocation
      });

      // Mark one-time gestures as sent (smile, nod, boogie)
      if (gestureType !== 'speak_to') {
        setSentGestures(prev => new Set(prev).add(gestureType));
      }

      // Track event on success
      trackEvent(EventName.GESTURE_CREATED, {
        gestureType: gestureType,
        targetUserId: targetUserId,
        hasText: textToSend.length > 0
      });

      // Notify parent with the created gesture
      if (onGestureCreated) {
        onGestureCreated(createdGesture);
      }
    } catch (err) {
      console.error('Error creating gesture:', err);
      // On error, don't mark as sent (allows retry)
    } finally {
      setIsSending(false);
    }
  };

  // Get button disabled state (visual only - clicks blocked by handler)
  const isButtonDisabled = (gestureType: GestureType) => {
    // Don't show disabled state during animation (let it play fully)
    if (animatingButton === gestureType) return false;
    // One-time gestures (smile, nod, boogie) - show disabled after sent
    if (gestureType !== 'speak_to' && sentGestures.has(gestureType)) return true;
    // speak_to requires text
    if (gestureType === 'speak_to' && !gestureText.trim()) return true;
    return false;
  };

  return (
    <div className="w-full max-w-3xl mx-auto px-6 py-6">
      {/* Quick gesture buttons - one-click send */}
      <div className="flex flex-wrap items-center justify-center gap-4 mb-6">
        {quickGestureTypes.map(({ type, label, animationClass }) => (
          <button
            key={type}
            onClick={() => handleGestureSend(type, label)}
            disabled={isButtonDisabled(type)}
            className={`gesture-button-container ${animationClass} ${animatingButton === type ? 'is-animating' : ''}`}
          >
            <span className="gesture-button-inner">
              {label}
            </span>
          </button>
        ))}
        {/* Share music button - only for user profiles */}
        {targetEntityType === 'user' && onShareMusicClick && (
          <button
            onClick={onShareMusicClick}
            className="gesture-button-container"
          >
            <span className="gesture-button-inner">
              Share Music
            </span>
          </button>
        )}
      </div>

      {/* Visual separator */}
      <div className="relative mb-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-gray-500 shadow-sm">or</span>
        </div>
      </div>

      {/* Message input with Speak to button */}
      <div className="relative">
        <textarea
          id="gesture-note"
          value={gestureText}
          onChange={(e) => setGestureText(e.target.value)}
          placeholder={`Say something to ${targetDisplayName}...`}
          className="w-full font-light text-base px-4 py-3 border border-gray-200/50 rounded-xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-coral-pink/20 focus:border-coral-pink/30 bg-white placeholder-gray-400 transition-all duration-200 resize-none"
          style={{ paddingRight: '112px', minHeight: '80px' }}
          rows={3}
        />
        <button
          onClick={() => handleGestureSend(speakToGesture.type, speakToGesture.label)}
          disabled={isButtonDisabled(speakToGesture.type)}
          className="gesture-button-container"
          style={{ position: 'absolute', right: '16px', bottom: '24px' }}
        >
          <span className="gesture-button-inner" style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}>
            {speakToGesture.label}
          </span>
        </button>
      </div>
    </div>
  );
};

export default GestureCreationPanel;
